/**
 * Counted-post-types popover (PRD "Counted post types", §5.1 #21).
 *
 * The one baseline-moving control, hoisted out of the Settings modal into the Metrics header so it
 * can be adjusted on the go. It is a **global setting**, never a request param (§5.2): every toggle
 * is a partial `PUT /api/settings`, debounced 600 ms, with no Save button.
 *
 * Two guards make that safe. The set-compare before mutating means a toggle-and-toggle-back never
 * sends `countedPostTypes`, so the server never enqueues a spurious `recompute_baselines` job
 * (§5.1 #21 fires the job on the field's *presence*). The last checked type is disabled, so the
 * list can never reach the empty set the server rejects with a 400.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { POST_TYPES, type PostType } from '@lw/shared';
import { describeError } from '../../api/client';
import { QK } from '../../api/queryKeys';
import { getStatus } from '../../api/status';
import { CheckboxList, ChecklistIcon, Menu, Spinner } from '../../components';
import { useDebouncedCallback } from '../../lib/hooks';
import { POST_TYPE_LABEL } from '../../lib/labels';
import { useSettingsQuery, useUpdateSettings } from '../settings/useSettings';

/** Matches the other icon-only header triggers. */
const ACTION_ICON = 13;

/** Long enough to absorb a burst of clicks, short enough to feel immediate. */
const SAVE_DEBOUNCE_MS = 600;

function sameStringSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((value, index) => value === right[index]);
}

/**
 * The recompute runs in the background; SSE invalidates the views when it finishes. Mounted only
 * inside the open panel, so the poll stops with it.
 */
function RecomputeHint() {
  const status = useQuery({
    queryKey: QK.status,
    queryFn: ({ signal }) => getStatus(signal),
    refetchInterval: 4000,
  });
  const activeJob = status.data?.activeJob ?? null;
  if (activeJob === null || activeJob.type !== 'recompute_baselines') return null;
  return (
    <p className="muted counted-panel__note" role="status">
      Recomputing baselines… {activeJob.progress.done} / {activeJob.progress.total}
    </p>
  );
}

export function CountedTypesMenu() {
  const settings = useSettingsQuery();
  const update = useUpdateSettings();
  const [draft, setDraft] = useState<PostType[] | null>(null);
  const value = draft ?? settings.data?.countedPostTypes ?? [];

  // Flushes on unmount (see the hook), so a toggle followed by closing the popover or leaving the
  // screen still lands.
  const save = useDebouncedCallback((next: PostType[]) => {
    const current = settings.data?.countedPostTypes;
    if (current === undefined || sameStringSet(next, current)) return;
    update.mutate(
      { countedPostTypes: next },
      {
        onSuccess: (saved) => {
          // A newer toggle that arrived while this PUT was in flight keeps its draft.
          setDraft((cur) =>
            cur !== null && sameStringSet(cur, saved.countedPostTypes) ? null : cur,
          );
        },
      },
    );
  }, SAVE_DEBOUNCE_MS);

  const toggle = (next: PostType[]) => {
    setDraft(next);
    save(next);
  };

  return (
    <Menu
      label={<ChecklistIcon size={ACTION_ICON} />}
      ariaLabel="Counted post types"
      align="right"
      triggerClassName="table-action table-action--icon"
      panelClassName="menu__panel--pad menu__panel--counted"
      disabled={settings.data === undefined}
      badge={<span className="count-badge">{value.length}</span>}
    >
      {() => (
        <div className="counted-panel">
          <p className="field__hint">
            Feeds baselines, averages, aggregates and the posting heatmap; excluded types stay
            visible in Posts and Feed. Changes recompute all derived numbers.
          </p>
          <CheckboxList
            options={POST_TYPES.map((type) => ({
              value: type,
              label: POST_TYPE_LABEL[type],
              // The server rejects an empty list (400) — keep the last one checked.
              disabled: value.length === 1 && value[0] === type,
            }))}
            selected={value}
            onChange={toggle}
          />
          {update.isPending ? (
            <p className="counted-panel__note" role="status">
              <Spinner size="sm" />
              <span className="muted">Saving…</span>
            </p>
          ) : null}
          {update.isError ? (
            <p className="form-error" role="alert">
              {describeError(update.error)}
            </p>
          ) : null}
          <RecomputeHint />
        </div>
      )}
    </Menu>
  );
}

export default CountedTypesMenu;
